import { useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { ENGINE_TYPES, formatSpecificationLabel, SPECIFICATION_DEFINITIONS, SPECIFICATION_KEYS, STATUSES } from '../constants';
import type { EngineTypeKey, MachineSpecificationValue, StatusKey } from '../types';
import { IMPORT_LABELS as LABELS, SPECIFICATION_LABELS } from '../../../utils/labels';

export interface ImportResult {
  added: number;
  skipped: number;
  errors: string[];
  warnings: string[];
}

interface UseImportMachinesReturn {
  downloading: boolean;
  importing: boolean;
  importResult: ImportResult | null;
  downloadTemplate(): Promise<void>;
  importMachines(file: File): Promise<ImportResult>;
  resetImport(): void;
}

const STATUS_MAP: Record<string, StatusKey> = Object.fromEntries([
  ...Object.entries(STATUSES).map(([key, { label }]) => [label.toLowerCase(), key as StatusKey]),
  ...Object.keys(STATUSES).map((key) => [key, key as StatusKey]),
]);

const ENGINE_TYPE_MAP: Record<string, EngineTypeKey> = Object.fromEntries([
  ...Object.entries(ENGINE_TYPES).map(([key, { label }]) => [label.toLowerCase(), key as EngineTypeKey]),
  ...Object.keys(ENGINE_TYPES).map((key) => [key, key as EngineTypeKey]),
]);

const PICKUP_YES_VALUES = new Set(['ja', 'yes', 'true', '1']);
const PICKUP_NO_VALUES = new Set(['nee', 'no', 'false', '0']);

// Accepts JS Date (from cellDates:true) or text string (DD/MM/YYYY or D/MM/YYYY etc.)
function parseDate(raw: unknown): string | null {
  if (raw instanceof Date) {
    const y = raw.getUTCFullYear();
    const m = String(raw.getUTCMonth() + 1).padStart(2, '0');
    const d = String(raw.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  if (typeof raw !== 'string' || !raw) return null;
  const match = raw.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return null;
  const [, d, m, y] = match;
  return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
}

// Accepts JS Date (Excel time stored as fraction of day) or text string (HH:mm)
function parseTime(raw: unknown): string | null {
  if (raw instanceof Date) {
    const h = String(raw.getUTCHours()).padStart(2, '0');
    const m = String(raw.getUTCMinutes()).padStart(2, '0');
    return `${h}:${m}`;
  }
  if (typeof raw !== 'string' || !raw) return null;
  if (!raw.trim().match(/^\d{2}:\d{2}$/)) return null;
  return raw.trim();
}

// Accepts a numeric cell (SheetJS gives numbers directly) or text (with either a dot or a
// comma as decimal separator, since users may paste Dutch-formatted numbers). Blank cells are
// "no value", not "invalid" — the caller distinguishes those before calling this.
function parseSpecValue(raw: unknown): number | null {
  const num = typeof raw === 'number' ? raw : parseFloat(String(raw).trim().replace(',', '.'));
  return Number.isFinite(num) ? num : null;
}

interface ParsedRow {
  name: string;
  category: string | null;
  machineNumber: string | null;
  optionNames: string[];
  status: StatusKey;
  engineType: EngineTypeKey;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  notes: string | null;
  supplierId: string | null;
  addressId: string | null;
  pickupOnEndDate: boolean;
  specifications: MachineSpecificationValue[];
}

export function useImportMachines(projectId: string | null): UseImportMachinesReturn {
  const [downloading, setDownloading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  const resetImport = () => {
    setImportResult(null);
  };

  const downloadTemplate = async (): Promise<void> => {
    if (!projectId) return;
    setDownloading(true);
    try {
      const XLSX = await import('xlsx');

      const { data: project } = await supabase
        .from('projects')
        .select('organization_id')
        .eq('id', projectId)
        .single();
      const orgId = project?.organization_id ?? null;

      const [suppliersResult, addressesResult, categoriesResult, optionsResult] = await Promise.all([
        orgId
          ? supabase
              .from('suppliers')
              .select('name')
              .eq('organization_id', orgId)
              .eq('archived', false)
              .order('name')
          : Promise.resolve({ data: [] as { name: string }[] }),
        supabase
          .from('project_addresses')
          .select('name')
          .eq('project_id', projectId)
          .order('created_at'),
        orgId
          ? supabase
              .from('categories')
              .select('name')
              .eq('organization_id', orgId)
              .eq('archived', false)
              .order('name')
          : Promise.resolve({ data: [] as { name: string }[] }),
        orgId
          ? supabase
              .from('options')
              .select('name')
              .eq('organization_id', orgId)
              .eq('archived', false)
              .order('name')
          : Promise.resolve({ data: [] as { name: string }[] }),
      ]);

      const suppliers = (suppliersResult.data ?? []) as { name: string }[];
      const addresses = (addressesResult.data ?? []) as { name: string }[];
      const categories = (categoriesResult.data ?? []) as { name: string }[];
      const options = (optionsResult.data ?? []) as { name: string }[];

      const wb = XLSX.utils.book_new();

      const machineHeaders = [
        LABELS.colName,
        LABELS.colCategory,
        LABELS.colOptions,
        LABELS.colEngineType,
        LABELS.colMachineNumber,
        LABELS.colStatus,
        LABELS.colStartDate,
        LABELS.colStartTime,
        LABELS.colEndDate,
        LABELS.colEndTime,
        LABELS.colPickupOnEndDate,
        LABELS.colSupplierName,
        LABELS.colAddressName,
        LABELS.colNotes,
        ...SPECIFICATION_KEYS.map(formatSpecificationLabel),
      ];
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.aoa_to_sheet([machineHeaders]),
        LABELS.sheetMachines,
      );

      const statusRows: string[][] = [
        [LABELS.statusesInstruction],
        [],
        ...Object.values(STATUSES).map((s) => [s.label]),
      ];
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.aoa_to_sheet(statusRows),
        LABELS.sheetStatuses,
      );

      const engineTypeRows: string[][] = [
        [LABELS.engineTypesInstruction],
        [],
        ...Object.values(ENGINE_TYPES).map((t) => [t.label]),
      ];
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.aoa_to_sheet(engineTypeRows),
        LABELS.sheetEngineTypes,
      );

      const supplierRows: string[][] = [
        [LABELS.suppliersInstruction],
        [],
        ...suppliers.map((s) => [s.name]),
      ];
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.aoa_to_sheet(supplierRows),
        LABELS.sheetSuppliers,
      );

      const addressRows: string[][] = [
        [LABELS.addressesInstruction],
        [],
        ...addresses.map((a) => [a.name]),
      ];
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.aoa_to_sheet(addressRows),
        LABELS.sheetAddresses,
      );

      const categoryRows: string[][] = [
        [LABELS.categoriesInstruction],
        [],
        ...categories.map((c) => [c.name]),
      ];
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.aoa_to_sheet(categoryRows),
        LABELS.sheetCategories,
      );

      const optionRows: string[][] = [
        [LABELS.optionsInstruction],
        [],
        ...options.map((o) => [o.name]),
      ];
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.aoa_to_sheet(optionRows),
        LABELS.sheetOptions,
      );

      const specificationRows: string[][] = [
        [LABELS.specificationsInstruction],
        [],
        [LABELS.colSpecTitle, LABELS.colSpecUnit, LABELS.colSpecDecimals],
        ...SPECIFICATION_KEYS.map((k) => [
          SPECIFICATION_LABELS[k],
          SPECIFICATION_DEFINITIONS[k].unit ?? '-',
          String(SPECIFICATION_DEFINITIONS[k].decimals),
        ]),
      ];
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.aoa_to_sheet(specificationRows),
        LABELS.sheetSpecifications,
      );

      XLSX.writeFile(wb, LABELS.templateFilename);
    } finally {
      setDownloading(false);
    }
  };

  const importMachines = async (file: File): Promise<ImportResult> => {
    if (!projectId) return { added: 0, skipped: 0, errors: [], warnings: [] };
    setImporting(true);
    setImportResult(null);
    try {
      const XLSX = await import('xlsx');
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer, { type: 'array', cellDates: true });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const allRows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
        defval: '',
        raw: true,
      });

      const rows = allRows.filter((row) =>
        Object.values(row).some((v) => v !== '' && v !== null && v !== undefined),
      );

      let orgId: string | null = null;
      const supplierNameToId = new Map<string, string>();
      const addressNameToId = new Map<string, string>();
      const categoryNameToId = new Map<string, string>();
      const optionNameToId = new Map<string, string>();

      if (rows.length > 0) {
        const { data: projectData } = await supabase
          .from('projects')
          .select('organization_id')
          .eq('id', projectId)
          .single();
        orgId = (projectData as { organization_id: string } | null)?.organization_id ?? null;

        const [suppliersResult, addressesResult, categoriesResult, optionsResult] = await Promise.all([
          orgId
            ? supabase.from('suppliers').select('id, name').eq('organization_id', orgId).eq('archived', false)
            : Promise.resolve({ data: [] as { id: string; name: string }[] }),
          supabase.from('project_addresses').select('id, name').eq('project_id', projectId),
          orgId
            ? supabase.from('categories').select('id, name').eq('organization_id', orgId).eq('archived', false)
            : Promise.resolve({ data: [] as { id: string; name: string }[] }),
          orgId
            ? supabase.from('options').select('id, name').eq('organization_id', orgId).eq('archived', false)
            : Promise.resolve({ data: [] as { id: string; name: string }[] }),
        ]);

        const suppliersData = (suppliersResult.data ?? []) as { id: string; name: string }[];
        suppliersData.forEach((s) => supplierNameToId.set(s.name.toLowerCase(), s.id));

        const addressesData = (addressesResult.data ?? []) as { id: string; name: string }[];
        addressesData.forEach((a) => addressNameToId.set(a.name.toLowerCase(), a.id));

        const categoriesData = (categoriesResult.data ?? []) as { id: string; name: string }[];
        categoriesData.forEach((c) => categoryNameToId.set(c.name.toLowerCase(), c.id));

        const optionsData = (optionsResult.data ?? []) as { id: string; name: string }[];
        optionsData.forEach((o) => optionNameToId.set(o.name.toLowerCase(), o.id));
      }

      const errors: string[] = [];
      const parsedRows: ParsedRow[] = [];
      const missingCategoryNames = new Set<string>();
      const missingOptionNames = new Set<string>();
      const invalidSpecWarnings: string[] = [];

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const rowNum = i + 2;

        const name = String(row[LABELS.colName] ?? '').trim();
        if (!name) {
          errors.push(LABELS.rowErrorNameRequired(rowNum));
          continue;
        }

        const rawStartDate = row[LABELS.colStartDate];
        const rawEndDate = row[LABELS.colEndDate];
        const rawStartTime = row[LABELS.colStartTime];
        const rawEndTime = row[LABELS.colEndTime];

        const hasStartDate = rawStartDate !== '' && rawStartDate != null;
        const hasEndDate = rawEndDate !== '' && rawEndDate != null;

        if (!hasStartDate) {
          errors.push(LABELS.rowErrorStartDateRequired(rowNum));
          continue;
        }
        if (!hasEndDate) {
          errors.push(LABELS.rowErrorEndDateRequired(rowNum));
          continue;
        }

        const startDate = parseDate(rawStartDate);
        const endDate = parseDate(rawEndDate);

        if (!startDate) {
          errors.push(LABELS.rowErrorInvalidDate(rowNum, LABELS.colStartDate));
          continue;
        }
        if (!endDate) {
          errors.push(LABELS.rowErrorInvalidDate(rowNum, LABELS.colEndDate));
          continue;
        }
        if (endDate < startDate) {
          errors.push(LABELS.rowErrorEndBeforeStart(rowNum));
          continue;
        }

        const hasStartTime = rawStartTime !== '' && rawStartTime != null;
        const hasEndTime = rawEndTime !== '' && rawEndTime != null;

        if (!hasStartTime) {
          errors.push(LABELS.rowErrorStartTimeRequired(rowNum));
          continue;
        }
        if (!hasEndTime) {
          errors.push(LABELS.rowErrorEndTimeRequired(rowNum));
          continue;
        }

        const startTime = parseTime(rawStartTime);
        const endTime = parseTime(rawEndTime);

        if (!startTime) {
          errors.push(LABELS.rowErrorInvalidTime(rowNum, LABELS.colStartTime));
          continue;
        }
        if (!endTime) {
          errors.push(LABELS.rowErrorInvalidTime(rowNum, LABELS.colEndTime));
          continue;
        }

        if (startDate === endDate && endTime <= startTime) {
          errors.push(LABELS.rowErrorEndTimeBeforeStart(rowNum));
          continue;
        }

        const rawStatus = String(row[LABELS.colStatus] ?? '').trim().toLowerCase();
        const status = STATUS_MAP[rawStatus];
        if (!status) {
          errors.push(LABELS.rowErrorInvalidStatus(rowNum));
          continue;
        }

        const rawEngineType = String(row[LABELS.colEngineType] ?? '').trim().toLowerCase();
        const engineType = ENGINE_TYPE_MAP[rawEngineType];
        if (!engineType) {
          errors.push(LABELS.rowErrorInvalidEngineType(rowNum));
          continue;
        }

        const rawSupplierName = String(row[LABELS.colSupplierName] ?? '').trim();
        const supplierId = rawSupplierName ? supplierNameToId.get(rawSupplierName.toLowerCase()) ?? null : null;
        if (rawSupplierName && !supplierId) {
          errors.push(LABELS.rowErrorUnknownSupplier(rowNum, rawSupplierName));
          continue;
        }

        const rawAddressName = String(row[LABELS.colAddressName] ?? '').trim();
        const addressId = rawAddressName ? addressNameToId.get(rawAddressName.toLowerCase()) ?? null : null;
        if (rawAddressName && !addressId) {
          errors.push(LABELS.rowErrorUnknownAddress(rowNum, rawAddressName));
          continue;
        }

        const rawPickup = String(row[LABELS.colPickupOnEndDate] ?? '').trim().toLowerCase();
        let pickupOnEndDate = false;
        if (rawPickup) {
          if (PICKUP_YES_VALUES.has(rawPickup)) {
            pickupOnEndDate = true;
          } else if (PICKUP_NO_VALUES.has(rawPickup)) {
            pickupOnEndDate = false;
          } else {
            errors.push(LABELS.rowErrorInvalidPickup(rowNum));
            continue;
          }
        }

        const category = String(row[LABELS.colCategory] ?? '').trim() || null;
        if (category && !categoryNameToId.has(category.toLowerCase())) {
          missingCategoryNames.add(category);
        }

        const rawOptionsText = String(row[LABELS.colOptions] ?? '').trim();
        const optionNames = rawOptionsText
          ? rawOptionsText.split(',').map((s) => s.trim()).filter(Boolean)
          : [];
        optionNames.forEach((optionName) => {
          if (!optionNameToId.has(optionName.toLowerCase())) missingOptionNames.add(optionName);
        });

        // Stored regardless of whether the row's category actually configures this key —
        // same lenient "extra data is fine" treatment as an unmatched category/option above.
        // An unparseable value is skipped (not fatal to the row) with a warning, not an error.
        const specifications: MachineSpecificationValue[] = [];
        SPECIFICATION_KEYS.forEach((k) => {
          const raw = row[formatSpecificationLabel(k)];
          if (raw === '' || raw === null || raw === undefined) return;
          const parsed = parseSpecValue(raw);
          if (parsed === null) {
            invalidSpecWarnings.push(LABELS.warningInvalidSpecification(rowNum, formatSpecificationLabel(k)));
            return;
          }
          const { decimals } = SPECIFICATION_DEFINITIONS[k];
          const factor = 10 ** decimals;
          specifications.push({ key: k, value: Math.round(parsed * factor) / factor });
        });

        parsedRows.push({
          name,
          category,
          machineNumber: String(row[LABELS.colMachineNumber] ?? '').trim() || null,
          optionNames,
          status,
          engineType,
          startDate,
          startTime,
          endDate,
          endTime,
          notes: String(row[LABELS.colNotes] ?? '').trim() || null,
          supplierId,
          addressId,
          pickupOnEndDate,
          specifications,
        });
      }

      const warnings: string[] = [
        ...[...missingCategoryNames].map((name) => LABELS.warningUnknownCategory(name)),
        ...[...missingOptionNames].map((name) => LABELS.warningUnknownOption(name)),
        ...invalidSpecWarnings,
      ];

      const toInsert: Record<string, unknown>[] = [];
      const rowOptionIds: string[][] = [];
      const rowSpecifications: MachineSpecificationValue[][] = [];

      for (const p of parsedRows) {
        const categoryId = p.category ? categoryNameToId.get(p.category.toLowerCase()) ?? null : null;
        const optionIds: string[] = [];
        const freeTextOptions: string[] = [];
        p.optionNames.forEach((optionName) => {
          const optionId = optionNameToId.get(optionName.toLowerCase());
          if (optionId) optionIds.push(optionId);
          else freeTextOptions.push(optionName);
        });

        toInsert.push({
          project_id: projectId,
          name: p.name,
          category: p.category,
          category_id: categoryId,
          machine_number: p.machineNumber,
          options: freeTextOptions,
          status: p.status,
          engine_type: p.engineType,
          start_date: p.startDate,
          start_time: p.startTime,
          end_date: p.endDate,
          end_time: p.endTime,
          notes: p.notes,
          supplier_id: p.supplierId,
          address_id: p.addressId,
          pickup_on_end_date: p.pickupOnEndDate,
        });
        rowOptionIds.push(optionIds);
        rowSpecifications.push(p.specifications);
      }

      if (toInsert.length > 0) {
        const { data: existing } = await supabase
          .from('machines')
          .select('sort_order')
          .eq('project_id', projectId)
          .order('sort_order', { ascending: false })
          .limit(1);
        const maxOrder = (existing?.[0]?.sort_order as number | null) ?? 0;
        toInsert.forEach((row, i) => {
          row.sort_order = maxOrder + i + 1;
        });
        const { data: insertedMachines, error } = await supabase.from('machines').insert(toInsert).select('id');
        if (error) throw error;

        const machineOptionsRows = ((insertedMachines ?? []) as { id: string }[]).flatMap((m, i) =>
          rowOptionIds[i].map((optionId) => ({ machine_id: m.id, option_id: optionId, organization_id: orgId })),
        );
        if (machineOptionsRows.length > 0) {
          const { error: optionsError } = await supabase.from('machine_options').insert(machineOptionsRows);
          if (optionsError) throw optionsError;
        }

        const machineSpecificationRows = ((insertedMachines ?? []) as { id: string }[]).flatMap((m, i) =>
          rowSpecifications[i].map((s) => ({
            machine_id: m.id,
            specification_key: s.key,
            value: s.value,
            organization_id: orgId,
          })),
        );
        if (machineSpecificationRows.length > 0) {
          const { error: specsError } = await supabase.from('machine_specifications').insert(machineSpecificationRows);
          if (specsError) throw specsError;
        }
      }

      const result: ImportResult = { added: toInsert.length, skipped: errors.length, errors, warnings };
      setImportResult(result);
      return result;
    } finally {
      setImporting(false);
    }
  };

  return { downloading, importing, importResult, downloadTemplate, importMachines, resetImport };
}
